import { useState } from "react";
import { createCheckout } from "@/lib/api";
import { useAuth, loginWithGoogle } from "@/lib/auth";
import { Check, Loader2, Sparkles, Lock } from "lucide-react";
import { toast } from "sonner";

const FEATURES = [
  "Crea ofertas estratégicas con 4 packs y precios anclados a tu presupuesto",
  "Analiza clientes y predice riesgo de sobretrabajo",
  "Escribe propuestas premium en español",
  "Detecta inflación de scope e infravaloración",
  "Biblioteca personal con todo lo que generes",
  "Powered by Claude Sonnet 4.5",
];

export default function UpgradeScreen({ reason = "trial" }) {
  // reason: "trial" | "expired" | "guest"
  const { user } = useAuth();
  const [plan, setPlan] = useState("yearly");
  const [loading, setLoading] = useState(false);

  const heading = reason === "expired"
    ? "Tu prueba ha terminado"
    : reason === "guest"
    ? "Empieza tu prueba de 5 días"
    : "Elige tu plan";

  const sub = reason === "expired"
    ? "Suscríbete para seguir creando ofertas, analizar clientes y generar propuestas."
    : reason === "guest"
    ? "Acceso completo durante 5 días. Cancela cuando quieras antes del día 6 y no se te cobra nada."
    : "5 días gratis para probar. Cancela cuando quieras antes del día 6.";

  const onSubscribe = async () => {
    if (!user) {
      loginWithGoogle();
      return;
    }
    setLoading(true);
    try {
      const r = await createCheckout(plan);
      window.location.href = r.url;
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "No se pudo iniciar el pago");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background px-6 py-12">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <span className="deco-star text-brand-purple w-5 h-5" />
          <span className="font-display text-xl">
            <span className="font-black">strato</span>
            <span className="font-normal text-foreground/55 ml-1.5 text-sm">by abstudio</span>
          </span>
        </div>

        <div className="block block-tea p-10 md:p-14 relative overflow-hidden">
          <span className="deco-star text-brand-lime w-12 h-12 absolute top-6 right-8 animate-spin-slow" />
          <div className="pill pill-lime text-[11px] mb-4">
            <Lock className="w-3.5 h-3.5" /> {reason === "expired" ? "Suscripción requerida" : "5 días de prueba gratis"}
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-[-0.025em] leading-[1]" data-testid="upgrade-heading">
            {heading}
          </h1>
          <p className="mt-5 text-base md:text-lg max-w-2xl opacity-90 leading-relaxed">{sub}</p>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-5 mt-8">
          <button
            onClick={() => setPlan("monthly")}
            data-testid="plan-monthly"
            className={`block p-7 text-left relative transition-all ${plan === "monthly" ? "block-purple ring-2 ring-brand-ink ring-offset-4 ring-offset-background" : "block-cream"}`}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-2xl font-black">Mensual</h3>
              {plan === "monthly" && <div className="bg-brand-ink text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">Elegido</div>}
            </div>
            <div className="flex items-baseline gap-1">
              <span className="font-display text-5xl font-black">$6</span>
              <span className="text-sm opacity-70">/mes</span>
            </div>
            <p className="text-sm mt-3 opacity-75">Flexible. Cancela cuando quieras.</p>
          </button>

          <button
            onClick={() => setPlan("yearly")}
            data-testid="plan-yearly"
            className={`block p-7 text-left relative transition-all ${plan === "yearly" ? "block-purple ring-2 ring-brand-ink ring-offset-4 ring-offset-background" : "block-cream"}`}
          >
            <div className="absolute -top-3 left-7 bg-brand-lime text-brand-ink text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider z-10">Ahorras 32%</div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display text-2xl font-black">Anual</h3>
              {plan === "yearly" && <div className="bg-brand-ink text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">Elegido</div>}
            </div>
            <div className="flex items-baseline gap-1">
              <span className="font-display text-5xl font-black">$49</span>
              <span className="text-sm opacity-70">/año</span>
            </div>
            <p className="text-sm mt-3 opacity-75">Equivale a $4.08/mes. Te ahorras $23.</p>
          </button>
        </div>

        {/* Features */}
        <div className="soft-card p-6 mt-6">
          <div className="text-[11px] uppercase tracking-wider font-bold text-foreground/55 mb-3">Incluye en ambos planes</div>
          <div className="grid sm:grid-cols-2 gap-2">
            {FEATURES.map((f) => (
              <div key={f} className="text-sm flex gap-2"><Check className="w-4 h-4 text-brand-tea mt-0.5 shrink-0" strokeWidth={2.5} /> {f}</div>
            ))}
          </div>
        </div>

        <button
          onClick={onSubscribe}
          disabled={loading}
          data-testid="subscribe-btn"
          className="mt-6 btn-pill w-full bg-brand-purple text-white hover:bg-brand-purpleDeep text-base h-14 justify-center disabled:opacity-60"
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Llevándote al pago seguro…</>
          ) : !user ? (
            <><Sparkles className="w-4 h-4" /> Entrar y empezar mi prueba</>
          ) : (
            <><Sparkles className="w-4 h-4" /> Empezar mi prueba de 5 días</>
          )}
        </button>
        <p className="text-xs text-foreground/55 text-center mt-3">
          Pago seguro con Stripe. Te pedimos tarjeta para evitar fricción al final del trial; no se te cobra nada los primeros 5 días y puedes cancelar 1 click antes.
        </p>
      </div>
    </div>
  );
}
